import {
  BUYBACK_MAX_ITEMS,
  BUYBACK_MAX_NEEDS_FIX_COUNT,
  BUYBACK_MAXIMUM_PRICE,
  BUYBACK_MIN_AGE,
  BUYBACK_MINIMUM_PRICE,
  BUYBACK_SHIPPING_DEADLINE_DAYS,
  IdentificationKind,
  requiredIdentificationSides,
  textIdentificationKind,
  textIdentificationSide,
} from '@maru44/huntre-utils/src/models/deviceBuyback'
import { Alert, Box, Divider, Paper, Stack, Typography } from '@mui/material'
import { kobutsuLicenseText } from 'src/config'

/**
 * 買取の説明。申請前に知っておくべきことを 1 箇所にまとめる。
 *
 * **数値はすべて huntre_utils の定数から引く。** 画面に数字を直書きすると
 * 条件を変えたときにここだけ古くなり、「画面に書いてあった条件」と
 * 実際の挙動がズレる。金額・期限・台数・年齢はすべて定数経由。
 *
 * **送料はお客様負担。** 最低保証が 1 台 1,000 円なので、1 台だけだと送料で
 * 相殺されうる。申込前に気付けるよう、金額の節にはっきり書く。
 * 発送直前の承認メール (notifyBuybackStatus) にも同じことを書いてある。
 *
 * 一覧ページと申請ページの両方から使う。文面を 2 箇所に持たないため。
 */
export const BuybackGuide = () => {
  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            デバイスの買取について
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, lineHeight: 1.8 }}>
            お使いにならなくなったワナップのデバイスを、当社が買い取ります。 お申し込みのあと本人確認を行い、確認が済んでから発送していただく流れです。
            買取金額はデバイスが届いてからの査定で決まります。
          </Typography>
        </Box>

        <Section title="買取金額">
          <Bullet>
            金額は<strong>到着後の査定</strong>で決まります。申し込みの時点では確定しません
          </Bullet>
          <Bullet>
            1 台あたり<strong>最低 {BUYBACK_MINIMUM_PRICE.toLocaleString()} 円を保証</strong>します。査定がこれを下回ることはありません
          </Bullet>
          <Bullet>1 台あたりの上限は {BUYBACK_MAXIMUM_PRICE.toLocaleString()} 円です</Bullet>
          <Bullet>
            1 回のお申し込みで最大 {BUYBACK_MAX_ITEMS} 台まで出せます。査定は 1 台ずつ行い、<strong>お振り込みは合計額を 1 回</strong>にまとめます
          </Bullet>
          <Bullet>
            <strong>当社への送料はお客様のご負担</strong>となります。買取金額から差し引くことはいたしません
          </Bullet>
        </Section>

        <Alert severity="warning">
          送料はお客様のご負担です。最低保証は 1 台あたり {BUYBACK_MINIMUM_PRICE.toLocaleString()} 円のため、
          <strong>1 台だけをお送りいただく場合は送料のほうが高くなることがあります。</strong>
          複数台をまとめてお送りいただくと 1 回の送料で済みます。
        </Alert>

        <Section title="お申し込みから入金までの流れ">
          <Step n={1} title="お申し込み">
            デバイスを選び、お客様の情報・本人確認書類・振込先口座をご登録ください
          </Step>
          <Step n={2} title="本人確認">
            当社が書類を確認します。不備があった場合は差し戻しとなり、書類を入れ直していただきます (最大 {BUYBACK_MAX_NEEDS_FIX_COUNT} 回)
          </Step>
          <Step n={3} title="承認・送付先のご案内">
            確認が済むと送付先をお知らせします。<strong>それまでデバイスは発送しないでください</strong>
          </Step>
          <Step n={4} title="ご発送">
            承認から <strong>{BUYBACK_SHIPPING_DEADLINE_DAYS} 日以内</strong>にご発送ください。過ぎるとお申し込みは失効します
          </Step>
          <Step n={5} title="査定">
            到着後、1 台ずつ査定して金額を確定します
          </Step>
          <Step n={6} title="お振り込み">
            合計額をご登録の口座へお振り込みします
          </Step>
        </Section>

        <Section title="ご用意いただくもの">
          <Bullet>
            <strong>本人確認書類の画像</strong>
            <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
              {Object.values(IdentificationKind).map((kind) => (
                <Box component="li" key={kind}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {textIdentificationKind(kind)} —{' '}
                    {requiredIdentificationSides(kind)
                      .map((side) => textIdentificationSide(kind, side))
                      .join(' と ')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Bullet>
          <Bullet>
            <strong>申請者ご本人名義の銀行口座</strong>。本人確認書類と名義が一致している必要があります
          </Bullet>
          <Bullet>お名前・フリガナ・ご住所・ご職業・生年月日</Bullet>
        </Section>

        <Section title="ご注意">
          <Bullet>
            <strong>個人のお客様のみ</strong>が対象です。法人からの買取は承っておりません
          </Bullet>
          <Bullet>{BUYBACK_MIN_AGE} 歳未満の方からは買い取りできません</Bullet>
          <Bullet>
            <strong>口座の名義が本人確認書類と異なる場合はお受けできません。</strong>
            対面でないお取引では、書類の確認とご本人名義口座へのお振り込みを合わせて本人確認とする必要があるためです
          </Bullet>
          <Bullet>キャンセルは発送前までです。発送後のキャンセルは承れません</Bullet>
          <Bullet>
            デバイスの到着後、そのデバイスはお客様のアカウントから外れます。<strong>メモと位置情報の履歴も削除されます</strong>
          </Bullet>
        </Section>

        <Divider />

        <Alert severity="info" icon={false}>
          <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
            当社は古物営業法に基づき、お取引の際にご本人の確認と記録を行っています。 本人確認書類の画像はお取引後 1 ヶ月で削除し、お取引の記録は法令に従い 3
            年間保存します。
            <br />
            古物商許可 {kobutsuLicenseText}
          </Typography>
        </Alert>
      </Stack>
    </Paper>
  )
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={0.75}>{children}</Stack>
  </Box>
)

const Bullet = ({ children }: { children: React.ReactNode }) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      ・
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
      {children}
    </Typography>
  </Stack>
)

const Step = ({ n, title, children }: { n: number; title: string; children: React.ReactNode }) => (
  <Stack direction="row" spacing={1.5}>
    <Box
      sx={{
        flexShrink: 0,
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {n}
    </Box>
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
        {children}
      </Typography>
    </Box>
  </Stack>
)
